/**
 * The form error block, and the state hook that feeds it.
 *
 * WHY THIS EXISTS. Every form in this app renders its error near the
 * top; every form's submit button is at the bottom, under a floating
 * nav bar. On a phone that means a validation failure is drawn entirely
 * off-screen while the user stares at a button that appears to have
 * done nothing. On MyChargerDetailPage the error sat ~270 lines above
 * Save, and the resulting silent failure cost Warren six test cycles
 * before CloudWatch proved no request was leaving the device at all.
 *
 * An error the user cannot see is the same as no error at all.
 *
 * ── The repeat-failure bug (2026-08-10) ──────────────────────────────
 *
 * Mount-scroll alone was not enough, and the reason is React batching,
 * not the scroll effect. A submit handler clears then re-sets:
 *
 *     setErrorMsg('');                      // top of handleSubmit
 *     ...
 *     setErrorMsg('Set a guest price…');    // a guard, same tick
 *
 * With no `await` between them these land in ONE batch, so React sees
 * the state go 'X' -> 'X' and bails out of rendering entirely. Nothing
 * re-renders, FormError never unmounts, the scroll never re-fires, and
 * `role="alert"` never re-announces. The FIRST failure is loud; every
 * identical repeat after it is completely silent.
 *
 * That is the exact signature Warren hit — the same guard, over and
 * over, with the screen saying nothing after the first time. It is a
 * general hazard: any form that clears-then-sets in one tick has it.
 *
 * THE FIX is a monotonic nonce carried in the state itself, so two
 * identical messages are still two DIFFERENT states and cannot be
 * batched away. The nonce has to come from the SETTER — by the time a
 * component could compare props, React has already bailed and there is
 * no render to observe. That is why `useFormError` lives here next to
 * the component rather than being left to each page: a page cannot opt
 * into correctness it has to remember.
 *
 * `block: 'center'` rather than 'start' keeps it clear of the sticky
 * header, and `prefers-reduced-motion` drops the smooth behaviour
 * rather than the scroll: arriving instantly still beats not arriving.
 */
import {
  useCallback, useEffect, useRef, useState,
  type CSSProperties, type ReactElement, type ReactNode,
} from 'react';
import formStyles from '@/components/Form.module.css';

// The state rule lives in formErrorState.ts so it can be unit-tested
// without a DOM — this file imports a CSS module, and the web test
// runner is plain node --test.
import { createFormErrorSequencer, type FormErrorSignal } from './formErrorState';

export type { FormErrorSignal };
export type SetFormError = (message: string | null | undefined) => void;

/**
 * Form-error state that survives repeat identical failures.
 *
 * Drop-in for `useState<string>('')`: the setter takes the same
 * arguments, and an empty/null message clears. What changes is the
 * READ side — the value is a `FormErrorSignal | null`, so render
 * `error.message` and pass `error.nonce` to `<FormError announce>`.
 * That type change is deliberate: it makes tsc point at every site
 * that needs the nonce rather than letting one quietly keep the old
 * silent behaviour.
 */
export function useFormError(): [FormErrorSignal | null, SetFormError] {
  const [error, setErrorState] = useState<FormErrorSignal | null>(null);
  // Ref, not state: bumping the sequence must never itself schedule a
  // render, and it must survive being called twice inside one batch.
  // Assigned once, lazily — `useRef(createFormErrorSequencer())` would
  // build a throwaway closure on every render.
  const seqRef = useRef<ReturnType<typeof createFormErrorSequencer> | null>(null);
  if (seqRef.current === null) seqRef.current = createFormErrorSequencer();
  const nextSignal = seqRef.current;

  const setError = useCallback<SetFormError>(
    (message) => { setErrorState(nextSignal(message)); },
    [nextSignal],
  );

  return [error, setError];
}

export interface FormErrorProps {
  children: ReactNode;
  /** Extra classes, merged after the base. */
  className?: string;
  /**
   * The occurrence id from `useFormError` (`error.nonce`).
   *
   * Changing it re-scrolls and re-announces, which is what makes the
   * second and third identical failure land. Omit it only where the
   * error genuinely cannot repeat — a one-shot fetch failure — and
   * accept that a repeat would be silent.
   */
  announce?: number | string;
  /** Opt out of the scroll for errors already in the viewport by
   *  construction — a dialog, a short card. Rare; default is to scroll. */
  scrollIntoView?: boolean;
  style?: CSSProperties;
}

export function FormError({
  children,
  className,
  announce,
  scrollIntoView = true,
  style,
}: FormErrorProps): ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!scrollIntoView) return;
    const el = ref.current;
    if (!el) return;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    // Keyed on `announce`, so mounting AND every fresh occurrence
    // scrolls. `scrollIntoView` is deliberately not a dep — it is read
    // once at mount, and listing it would re-fire the scroll if a
    // parent toggled it mid-life. No eslint-disable here: this repo's
    // config does not register react-hooks/exhaustive-deps, so the
    // directive itself would fail lint as an unused disable.
  }, [announce]);

  return (
    <div
      ref={ref}
      // role="alert" on every one of them: several call sites had it,
      // several did not, and a screen reader should never depend on
      // which page you happen to be on.
      role="alert"
      className={className ? `${formStyles.formError} ${className}` : formStyles.formError}
      style={style}
    >
      {/* Keyed on the occurrence so identical text still remounts.
          Assistive tech announces a live region when its CONTENT
          changes; re-rendering the same string is not a change, so
          without this the repeat is silent to a screen reader even
          though the sighted user got a scroll. */}
      <span key={announce}>{children}</span>
    </div>
  );
}
